import type { AccessToken, GetTokenOptions, TokenCredential } from "@azure/identity";

/**
 * Reuse one access token across many calls on the same scope.
 *
 * WHY THIS EXISTS, measured rather than assumed. `AzureCliCredential` holds NO
 * token cache — `getToken` shells out to `az account get-access-token` every
 * single time (@azure/identity 4.13.1, azureCliCredential.js: getToken calls
 * getAzureCliAccessToken with no cache lookup on the path). `authedJsonResult`
 * calls `getToken` per request, so a poll doing N probes spawns N `az` processes.
 *
 * That is the half of a probe's cost a request timeout cannot touch: the
 * subprocess runs BEFORE the fetch, so `AbortSignal.timeout` never sees it. On
 * 2026-08-31 a 60-attempt poll took 34 minutes — about 34s per attempt — against
 * a declared budget that only ever counted its sleeps. Reusing the token removes
 * that per-probe spawn for every probe after the first.
 *
 * NOT A GENERAL-PURPOSE CACHE, and deliberately not installed globally: it is
 * for a caller that hits ONE scope in a tight, SEQUENTIAL loop over a bounded
 * window. It refuses a token inside `skewMs` of expiry, so a long poll cannot
 * start using a stale one mid-flight.
 *
 * WHAT THE KEY DOES AND DOES NOT COVER. It is scope + tenant, so a different
 * scope or tenant never receives another's token. It does NOT key on `claims` or
 * `enableCae`, so a CAE-challenge retry would be served the token it is
 * challenging — unreachable through `AzureCliCredential`, which rejects `claims`
 * outright, and stated rather than implied because a future inner credential
 * would make it reachable.
 *
 * NO IN-FLIGHT DEDUPE either: two CONCURRENT first calls both reach the inner
 * credential. The caller here is sequential, so "only the first call pays" holds
 * for it and would not hold for a parallel one.
 */
export function cachingCredential(inner: TokenCredential, skewMs = 5 * 60_000): TokenCredential {
  const cache = new Map<string, AccessToken>();
  return {
    async getToken(scopes: string | string[], options?: GetTokenOptions): Promise<AccessToken | null> {
      // A separator that cannot occur in a scope or tenant id. With a space,
      // ["a", "b c"] and ["a b", "c"] produce the same key.
      const key = `${Array.isArray(scopes) ? scopes.join("\u0000") : scopes}\u0000${options?.tenantId ?? ""}`;
      const hit = cache.get(key);
      if (hit && hit.expiresOnTimestamp - Date.now() > skewMs) return hit;
      const fresh = await inner.getToken(scopes, options);
      // Evict on a failed refresh. UNOBSERVABLE UNDER THE SEQUENTIAL CALLER this
      // credential is documented for: the only entry a null refresh can find is one
      // already inside `skewMs`, which the check above never serves.
      //
      // It is NOT unobservable in general, and an earlier version of this comment
      // wrongly said so. Interleave two calls and the state exists: A resolves a
      // good token and caches it, B's inner then resolves null and evicts the entry
      // A just wrote, and the next call re-spawns `az`. Keeping the entry would
      // serve A's still-valid token instead. Evicting is the safer arm of that
      // trade — a refresh that failed is a poor reason to keep trusting a cache —
      // and the honest scope of the "unobservable" claim is the sequential caller,
      // not the code.
      if (fresh) cache.set(key, fresh);
      else cache.delete(key);
      return fresh;
    },
  };
}
